import { defineComponent } from 'vue';
import { QBtn, QBanner, QInput, QSpinner } from 'quasar';
import { dia, shapes, linkTools } from '@joint/core';
import {
    mdiAlert,
    mdiAlertCircleOutline,
    mdiCodeBracesBox,
    mdiFolderFileOutline,
    mdiLightningBoltOutline,
    mdiPlus,
    mdiSitemapOutline,
    mdiWindowMaximize,
} from '@mdi/js';
import utils from '../../../../../Shared/utils.jsx';
import { SENTENCE_CASE_BUTTON_CLASS } from '../../../../../Shared/buttonAppearance.js';
import '../../../css/BasicButton.css';
import './projectEventMap.css';
import { loadFlowChannelList, resolveChannel } from '../debugger/flowChannelConfig.js';
import { resolveLogicalUri } from '../debugger/flowV2WebUrl.js';
import {
    buildMemberTree,
    catalogByName,
    connect,
    disconnect,
    eventCatalog,
    newMethodDraft,
    projectFullPath,
    signatureWarning,
    visibleEventNames,
} from './projectEventMap.js';

const ACCENTS = ['#3949ab', '#00897b', '#ef6c00', '#8e24aa', '#039be5', '#c62828', '#6d4c41', '#2e7d32'];
const EVENT_X = 28;
const EVENT_W = 248;
const EVENT_H = 48;
const ROW = 64;
const MAP_ROUTER = {
    name: 'metro',
    args: {
        startDirections: ['right'],
        endDirections: ['left'],
        excludeEnds: ['source', 'target'],
    },
};

const Card = dia.Element.define('eventmap.Card', {
    attrs: {
        root: { cursor: 'default' },
        body: {
            width: 'calc(w)',
            height: 'calc(h)',
            rx: 10,
            ry: 10,
            stroke: '#d5d9e8',
            strokeWidth: 1.5,
            fill: '#ffffff',
        },
        accent: {
            x: 8,
            y: 12,
            width: 3,
            height: 'calc(h - 24)',
            rx: 1.5,
            fill: '#c5cae9',
        },
        icon: {
            fill: '#3949ab',
            fillRule: 'evenodd',
            transform: 'translate(18, 13) scale(0.78)',
        },
        label: {
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            x: 46,
            y: 'calc(h / 2)',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'Segoe UI, Roboto, sans-serif',
            fill: '#1a237e',
        },
        caption: {
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            x: 46,
            y: 'calc(h - 14)',
            fontSize: 10,
            fontFamily: 'Segoe UI, Roboto, sans-serif',
            fill: '#78909c',
        },
    },
}, {
    markup: [
        { tagName: 'rect', selector: 'body' },
        { tagName: 'rect', selector: 'accent' },
        { tagName: 'path', selector: 'icon' },
        { tagName: 'circle', selector: 'warnBadge' },
        { tagName: 'path', selector: 'warnIcon' },
        { tagName: 'title', selector: 'warnTip' },
        { tagName: 'text', selector: 'label' },
        { tagName: 'text', selector: 'caption' },
    ],
});

function normalizeFolderItems(raw) {
    const list = Array.isArray(raw) ? raw : (raw?.Result || raw?.Rows || []);
    return Array.isArray(list) ? list : [];
}

function eventCellId(name) {
    return `event:${encodeURIComponent(name)}`;
}

function methodCellId(id) {
    return `method:${id}`;
}

function typeCaption(type) {
    if (type === 'APIHeader')
        return 'API';
    if (type === 'FlowHeader')
        return 'Flow';
    return '';
}

function methodIcon(type) {
    return type === 'APIHeader' ? mdiCodeBracesBox : mdiSitemapOutline;
}

function portGroup(side, magnet, stroke, fill) {
    return {
        position: { name: side },
        attrs: {
            portBody: {
                magnet: magnet ? true : false,
                r: 6.5,
                fill,
                stroke,
                strokeWidth: 2,
                cursor: magnet ? 'crosshair' : 'default',
            },
        },
        markup: [{ tagName: 'circle', selector: 'portBody' }],
    };
}

function singlePort(side, magnet, stroke, fill) {
    return {
        groups: {
            [side === 'right' ? 'out' : 'in']: portGroup(side, magnet, stroke, fill),
        },
        items: [{ id: side === 'right' ? 'out' : 'in', group: side === 'right' ? 'out' : 'in' }],
    };
}

export default defineComponent({
    name: 'flow-v2-project-event-map',
    components: { QBtn, QBanner, QInput, QSpinner },
    props: {
        model: { type: Object, required: true },
        schema: { type: Object, default: null },
        projectId: { type: String, default: '' },
        channel: { type: Object, default: null },
    },
    emits: ['create-method'],
    data() {
        return {
            folderItems: [],
            channelList: null,
            foldersReady: false,
            folderError: null,
            extraNames: [],
            customDraft: '',
            methodQuery: '',
            createHint: '',
            treeContentBottom: 0,
        };
    },
    computed: {
        linkedCount() {
            return (this.model?.Events || []).filter((row) => row?.EventName && row?.FlowID).length;
        },
        boardSignature() {
            const events = (this.model?.Events || [])
                .map((row) => `${row?.EventName || ''}=${row?.FlowID || ''}`)
                .join('|');
            const folders = (this.folderItems || [])
                .map((item) => `${item?.ID || ''}:${item?.Type || ''}:${item?.Name || ''}:${item?.Path || ''}:${item?.ReturnType || ''}:${Array.isArray(item?.Parameters) ? item.Parameters.length : ''}`)
                .join('|');
            const channelEvents = (this.resolvedChannel().FlowEvents || [])
                .map((event) => `${event?.Name || ''}:${event?.ReturnType || ''}:${Array.isArray(event?.Parameters) ? event.Parameters.length : ''}`)
                .join(',');
            return [
                this.foldersReady ? 'ready' : 'wait',
                this.channelList ? 'channels' : 'builtin',
                this.model?.ChannelType || '',
                this.model?.Name || '',
                this.model?.Path || '',
                channelEvents,
                events,
                (this.extraNames || []).join('|'),
                folders,
            ].join('#');
        },
    },
    watch: {
        projectId: {
            immediate: true,
            handler() {
                this.extraNames = [];
                this.customDraft = '';
                this.methodQuery = '';
                this.createHint = '';
                this.treeScroll = 0;
                this._sigAttempted = new Set();
                this._signatures = new Map();
                this.loadFolderItems();
                this.loadChannels();
            },
        },
        boardSignature() {
            this.scheduleRender();
        },
        methodQuery() {
            this.applySearch();
            this.scrollFirstMatchIntoView();
        },
    },
    mounted() {
        this.initPaper();
        this.scheduleRender();
        const stage = this.$refs.paperHost?.parentElement;
        if (stage) {
            this._onStageWheel = (evt) => this.onStageWheel(evt);
            stage.addEventListener('wheel', this._onStageWheel, { passive: false });
        }
        if (stage && typeof ResizeObserver !== 'undefined') {
            this._stageObserver = new ResizeObserver(() => {
                const width = stage.clientWidth;
                const height = stage.clientHeight;
                if (!width || !height)
                    return;
                const widthSame = this._stageWidth && Math.abs(width - this._stageWidth) < 24;
                const heightSame = this._stageHeight && Math.abs(height - this._stageHeight) < 24;
                if (widthSame && heightSame)
                    return;
                this._stageWidth = width;
                this._stageHeight = height;
                this.scheduleRender();
            });
            this._stageObserver.observe(stage);
        }
    },
    beforeUnmount() {
        this._folderRequest = -1;
        clearTimeout(this._toolTimer);
        this.stopLinkDrag();
        this._stageObserver?.disconnect();
        const stage = this.$refs.paperHost?.parentElement;
        if (stage && this._onStageWheel)
            stage.removeEventListener('wheel', this._onStageWheel);
        if (this.paper) {
            this.paper.remove();
            this.paper = null;
        }
        this.graph = null;
    },
    methods: {
        async loadFolderItems(quiet) {
            const request = (this._folderRequest || 0) + 1;
            this._folderRequest = request;
            if (!quiet) {
                this.foldersReady = false;
                this.folderError = null;
            }
            try {
                const raw = await utils.api.get('Apps/FlowV2/ListAllFolderItems');
                if (this._folderRequest !== request)
                    return;
                this.folderItems = normalizeFolderItems(raw);
            }
            catch (err) {
                if (this._folderRequest !== request || quiet)
                    return;
                this.folderError = err?.message || String(err);
                this.folderItems = [];
            }
            finally {
                if (this._folderRequest === request) {
                    this.foldersReady = true;
                    this.hydrateLinkedSignatures();
                }
            }
        },
        async loadChannels() {
            try {
                const list = await loadFlowChannelList((url) => utils.api.get(url));
                this.channelList = list;
            }
            catch {
                this.channelList = null;
            }
        },
        resolvedChannel() {
            const type = this.model?.ChannelType;
            const listed = Array.isArray(this.channelList) ? this.channelList : null;
            if (this.channel && this.channel.ChannelType === type)
                return resolveChannel(type, [this.channel, ...(listed || [])]);
            return resolveChannel(type, listed || undefined);
        },
        async hydrateLinkedSignatures() {
            const wanted = new Set((this.model?.Events || []).map((row) => row?.FlowID).filter(Boolean));
            const cache = this._signatures || (this._signatures = new Map());
            const attempted = this._sigAttempted || (this._sigAttempted = new Set());
            const pending = [];
            let changed = false;
            for (const item of this.folderItems || []) {
                if (!item || !wanted.has(item.ID) || (item.Type !== 'FlowHeader' && item.Type !== 'APIHeader'))
                    continue;
                const known = Object.prototype.hasOwnProperty.call(item, 'Parameters')
                    || Object.prototype.hasOwnProperty.call(item, 'ReturnType');
                if (known) {
                    cache.set(item.ID, {
                        Parameters: Array.isArray(item.Parameters) ? item.Parameters : [],
                        ReturnType: item.ReturnType == null ? '' : String(item.ReturnType),
                    });
                    continue;
                }
                const saved = cache.get(item.ID);
                if (saved) {
                    item.Parameters = saved.Parameters;
                    item.ReturnType = saved.ReturnType;
                    changed = true;
                    continue;
                }
                if (!attempted.has(item.ID))
                    pending.push(item);
            }
            if (pending.length) {
                await Promise.all(pending.map(async (item) => {
                    attempted.add(item.ID);
                    try {
                        const header = await utils.api.get(`Apps/FlowV2/GetMethodHeader?Type=${encodeURIComponent(item.Type)}&ID=${encodeURIComponent(item.ID)}`);
                        const signature = {
                            Parameters: Array.isArray(header?.Parameters) ? header.Parameters : [],
                            ReturnType: header?.ReturnType == null ? '' : String(header.ReturnType),
                        };
                        cache.set(item.ID, signature);
                        item.Parameters = signature.Parameters;
                        item.ReturnType = signature.ReturnType;
                        changed = true;
                    }
                    catch {
                        /* Leave the row unknown so a failed read does not look like an empty signature. */
                    }
                }));
            }
            if (changed)
                this.folderItems = this.folderItems.slice();
        },
        initPaper() {
            const host = this.$refs.paperHost;
            if (!host || this.paper)
                return;
            this.graph = new dia.Graph({}, { cellNamespace: shapes });
            this.paper = new dia.Paper({
                el: host,
                model: this.graph,
                width: host.parentElement?.clientWidth || 640,
                height: host.parentElement?.clientHeight || 480,
                cellViewNamespace: shapes,
                background: { color: '#f4f6fb' },
                frozen: true,
                async: false,
                interactive: (cellView) => {
                    const model = cellView.model;
                    if (model.isLink()) {
                        const kind = model.get('kind');
                        if (kind === 'tree' || kind === 'mapping')
                            return false;
                        return { vertexAdd: false, vertexMove: false, labelMove: false, linkMove: false };
                    }
                    return { elementMove: false };
                },
                linkPinning: false,
                snapLinks: { radius: 28 },
                markAvailable: true,
                defaultRouter: { name: MAP_ROUTER.name, args: { ...MAP_ROUTER.args } },
                defaultConnector: { name: 'rounded', args: { radius: 8 } },
                defaultLink: () => new shapes.standard.Link({
                    kind: 'draft',
                    attrs: {
                        line: {
                            stroke: '#5c6bc0',
                            strokeWidth: 2,
                            strokeDasharray: '6 4',
                            targetMarker: { type: 'path', d: 'M 8 -4 0 0 8 4 z', fill: '#5c6bc0' },
                        },
                    },
                }),
                validateMagnet: (cellView, magnet) => {
                    if (!magnet || magnet.getAttribute('magnet') === 'false')
                        return false;
                    return cellView.model.get('kind') === 'event';
                },
                validateConnection: (sourceView, _sourceMagnet, targetView, _targetMagnet, end) => {
                    if (end !== 'target' || !sourceView || !targetView)
                        return false;
                    if (sourceView.model.get('kind') !== 'event')
                        return false;
                    const target = targetView.model;
                    if (target.get('kind') === 'create-method')
                        return true;
                    return target.get('kind') === 'method' && target.get('dropTarget') === true;
                },
            });
            this.paper.on('link:connect', (linkView) => this.onLinkConnect(linkView));
            this.paper.on('link:pointermove', (linkView, evt) => this.onLinkDragMove(linkView, evt));
            this.paper.on('link:pointerup', () => this.stopLinkDrag());
            this.paper.on('link:mouseenter', (linkView) => this.onLinkEnter(linkView));
            this.paper.on('link:mouseleave', (linkView) => this.onLinkLeave(linkView));
            this.graph.on('remove', (cell) => this.onCellRemove(cell));
            this.paper.unfreeze();
        },
        scheduleRender() {
            if (this._renderQueued)
                return;
            this._renderQueued = true;
            this.$nextTick(() => {
                this._renderQueued = false;
                this.renderGraph();
            });
        },
        addCard(spec) {
            const hasCaption = !!spec.caption;
            const cell = new Card({
                id: spec.id,
                kind: spec.kind,
                eventName: spec.eventName || '',
                flowId: spec.flowId || '',
                memberName: spec.memberName || spec.label || '',
                dropTarget: spec.dropTarget === true,
                position: { x: spec.x, y: spec.y },
                size: { width: spec.w, height: spec.h },
                attrs: {
                    body: {
                        fill: spec.fill,
                        stroke: spec.stroke,
                        strokeWidth: spec.strokeWidth || 1.5,
                        strokeDasharray: spec.dash || 'none',
                    },
                    accent: { fill: spec.accent },
                    icon: { d: spec.icon || '', fill: spec.iconFill || spec.accent },
                    warnBadge: {
                        display: spec.warn ? 'block' : 'none',
                        cx: 38,
                        cy: 18,
                        r: 7,
                        fill: '#ef6c00',
                        stroke: '#ffffff',
                        strokeWidth: 1.5,
                    },
                    warnIcon: {
                        display: spec.warn ? 'block' : 'none',
                        d: mdiAlert,
                        fill: '#ffffff',
                        transform: 'translate(32.2, 12.2) scale(0.48)',
                    },
                    warnTip: { text: spec.warn || '' },
                    label: {
                        text: spec.label || '',
                        x: spec.icon ? 46 : 12,
                        y: hasCaption ? 18 : 'calc(h / 2)',
                        textWrap: { width: spec.w - (spec.icon ? 64 : 28), maxLineCount: 1, ellipsis: true },
                    },
                    caption: {
                        text: spec.caption || '',
                        textWrap: { width: spec.w - 64, maxLineCount: 1, ellipsis: true },
                    },
                },
                ports: spec.ports || { groups: {}, items: [] },
            });
            cell.set('layoutX', spec.x);
            cell.set('layoutY', spec.y);
            cell.set('className', spec.className || '');
            cell.set('methodPath', spec.methodPath || '');
            cell.set('existingNames', spec.existingNames || []);
            this.graph.addCell(cell);
            return cell;
        },
        addSpine(parent, child) {
            const x = parent.x + 14;
            const y1 = parent.y + parent.h;
            const y2 = child.y + (child.h / 2);
            const layoutGeom = {
                source: { x, y: y1 },
                target: { x: child.x, y: y2 },
                vertices: [{ x, y: y2 }],
            };
            this.graph.addCell(new shapes.standard.Link({
                kind: 'tree',
                layoutGeom,
                source: { x, y: y1 },
                target: { x: child.x, y: y2 },
                vertices: [{ x, y: y2 }],
                attrs: {
                    root: { pointerEvents: 'none' },
                    line: {
                        stroke: '#c5cbe0',
                        strokeWidth: 1.25,
                        targetMarker: null,
                        pointerEvents: 'none',
                    },
                    wrapper: { pointerEvents: 'none' },
                },
                router: { name: 'normal' },
                connector: { name: 'rounded', args: { radius: 6 } },
            }));
        },
        renderGraph() {
            if (!this.paper || !this.graph || !this.foldersReady || !this.model)
                return;
            const catalog = eventCatalog({ channel: this.resolvedChannel() });
            const catalogMap = catalogByName(catalog);
            const names = visibleEventNames(catalog, this.model.Events, this.extraNames);
            const tree = buildMemberTree(this.folderItems, this.model, this.model.Events);
            const mapped = new Map();
            for (const row of this.model.Events || []) {
                if (row?.EventName && row?.FlowID)
                    mapped.set(row.EventName, row.FlowID);
            }

            const measured = this.$refs.paperHost?.parentElement?.clientWidth || 0;
            const stageWidth = Math.max(1, measured);
            const rightPad = 20;
            const usable = Math.max(48, stageWidth - EVENT_X - rightPad);
            let eventW = Math.min(EVENT_W, Math.round(usable * 0.34));
            let gutter = Math.min(110, Math.round(usable * 0.14));
            if (usable - eventW - gutter < Math.round(usable * 0.4)) {
                gutter = Math.max(28, Math.min(gutter, Math.round(usable * 0.1)));
                eventW = Math.min(eventW, Math.round(usable * 0.3));
            }
            if (eventW + gutter + 40 > usable) {
                gutter = 24;
                eventW = Math.max(72, Math.round((usable - gutter) * 0.38));
            }
            const treeX = EVENT_X + eventW + gutter;
            const cardW = Math.min(320, Math.max(1, stageWidth - rightPad - treeX - 36));
            const width = stageWidth;

            let y = 16;
            const eventRows = names.map((name, index) => {
                const row = { name, index, x: EVENT_X, y, w: eventW, h: EVENT_H };
                y += ROW;
                return row;
            });
            const eventsBottom = y;

            y = 16;
            const projectRow = {
                x: treeX,
                y,
                w: cardW + 36,
                h: 48,
            };
            y += ROW;
            const classRows = [];
            const methodRows = [];
            const createRows = [];
            for (const cls of tree.classes) {
                const classRow = {
                    ...cls,
                    x: treeX + 18,
                    y,
                    w: cardW + 18,
                    h: 36,
                };
                classRows.push(classRow);
                y += 48;
                for (const method of cls.methods) {
                    methodRows.push({
                        ...method,
                        className: cls.name,
                        x: treeX + 44,
                        y,
                        w: cardW,
                        h: 50,
                    });
                    y += ROW;
                }
                createRows.push({
                    className: cls.name,
                    methodPath: cls.methodPath,
                    existingNames: cls.methods.map((method) => method.name),
                    x: treeX + 44,
                    y,
                    w: cardW,
                    h: 50,
                });
                y += ROW;
            }
            let outsideHeader = null;
            const outsideRows = [];
            if (tree.outside.length) {
                y += 6;
                outsideHeader = { x: treeX + 18, y, w: cardW + 18, h: 28 };
                y += 40;
                for (const method of tree.outside) {
                    outsideRows.push({
                        ...method,
                        x: treeX + 44,
                        y,
                        w: cardW,
                        h: 50,
                    });
                    y += ROW;
                }
            }
            y += 24;

            const hostHeight = this.$refs.paperHost?.parentElement?.clientHeight || 0;
            this.treeContentBottom = y;
            const height = Math.max(320, hostHeight || 0, eventsBottom + 24);

            this.rebuilding = true;
            this.paper.freeze();
            try {
                this.graph.clear();
                this.paper.setDimensions(width, height);

                eventRows.forEach((row) => {
                    const accent = ACCENTS[row.index % ACCENTS.length];
                    const linked = mapped.has(row.name);
                    this.addCard({
                        id: eventCellId(row.name),
                        kind: 'event',
                        eventName: row.name,
                        label: row.name,
                        x: row.x,
                        y: row.y,
                        w: row.w,
                        h: row.h,
                        fill: linked ? '#ffffff' : '#f8f9fd',
                        stroke: linked ? accent : '#d5d9e8',
                        strokeWidth: linked ? 2 : 1.5,
                        accent: linked ? accent : '#c5cae9',
                        icon: mdiLightningBoltOutline,
                        iconFill: linked ? accent : '#9aa3c7',
                        ports: singlePort('right', true, accent, linked ? accent : '#ffffff'),
                    });
                });

                this.addCard({
                    id: 'project-root',
                    kind: 'project',
                    label: tree.projectName || 'Project',
                    caption: tree.projectPath && tree.projectPath !== tree.projectName ? tree.projectPath : '',
                    memberName: tree.projectName,
                    x: projectRow.x,
                    y: projectRow.y,
                    w: projectRow.w,
                    h: projectRow.h,
                    fill: '#e8eaf6',
                    stroke: '#c5cae9',
                    accent: '#3949ab',
                    icon: mdiFolderFileOutline,
                    iconFill: '#3949ab',
                });

                for (const row of classRows) {
                    this.addCard({
                        id: `class:${encodeURIComponent(row.name)}`,
                        kind: 'class',
                        label: row.label,
                        memberName: row.label,
                        x: row.x,
                        y: row.y,
                        w: row.w,
                        h: row.h,
                        fill: '#f7f8fc',
                        stroke: '#e1e5f0',
                        accent: '#9fa8da',
                        icon: mdiWindowMaximize,
                        iconFill: '#5c6bc0',
                    });
                    this.addSpine(projectRow, row);
                }

                const warningFor = (row) => {
                    const notes = [];
                    for (const [eventName, flowId] of mapped) {
                        if (flowId !== row.id)
                            continue;
                        const warning = signatureWarning(catalogMap.get(eventName), row);
                        if (warning)
                            notes.push(`${eventName}: ${warning}`);
                    }
                    return notes.join('\n');
                };
                for (const row of methodRows) {
                    const parent = classRows.find((cls) => cls.name === row.className) || projectRow;
                    this.addCard({
                        id: methodCellId(row.id),
                        kind: 'method',
                        flowId: row.id,
                        dropTarget: true,
                        label: row.name,
                        caption: typeCaption(row.type),
                        memberName: row.name,
                        warn: warningFor(row),
                        x: row.x,
                        y: row.y,
                        w: row.w,
                        h: row.h,
                        fill: '#ffffff',
                        stroke: '#d5d9e8',
                        accent: row.type === 'APIHeader' ? '#00897b' : '#3949ab',
                        icon: methodIcon(row.type),
                        iconFill: row.type === 'APIHeader' ? '#00897b' : '#3949ab',
                        ports: singlePort('left', true, '#5c6bc0', '#e8eaf6'),
                    });
                    this.addSpine(parent, row);
                }
                for (const row of createRows) {
                    const parent = classRows.find((cls) => cls.name === row.className) || projectRow;
                    this.addCard({
                        id: `create:${encodeURIComponent(row.className)}`,
                        kind: 'create-method',
                        className: row.className,
                        methodPath: row.methodPath,
                        existingNames: row.existingNames,
                        label: 'New method',
                        caption: 'Drop an event',
                        x: row.x,
                        y: row.y,
                        w: row.w,
                        h: row.h,
                        fill: '#fafbff',
                        stroke: '#9fa8da',
                        dash: '6 4',
                        accent: '#7986cb',
                        icon: mdiPlus,
                        iconFill: '#5c6bc0',
                        ports: singlePort('left', true, '#5c6bc0', '#ffffff'),
                    });
                    this.addSpine(parent, row);
                }

                if (outsideHeader) {
                    this.addCard({
                        id: 'outside-header',
                        kind: 'note',
                        label: 'Outside this project',
                        x: outsideHeader.x,
                        y: outsideHeader.y,
                        w: outsideHeader.w,
                        h: outsideHeader.h,
                        fill: 'transparent',
                        stroke: 'transparent',
                        strokeWidth: 0,
                        accent: '#ffcc80',
                        icon: '',
                    });
                    for (const row of outsideRows) {
                        this.addCard({
                            id: methodCellId(row.id),
                            kind: 'method',
                            flowId: row.id,
                            dropTarget: false,
                            label: row.missing ? 'Missing method' : row.name,
                            caption: row.missing ? row.id : (typeCaption(row.type) || 'Outside this project'),
                            memberName: row.name,
                            x: row.x,
                            y: row.y,
                            w: row.w,
                            h: row.h,
                            fill: row.missing ? '#fafafa' : '#fff8f1',
                            stroke: row.missing ? '#b0bec5' : '#ffcc80',
                            dash: row.missing ? '4 3' : 'none',
                            accent: '#ef6c00',
                            icon: row.missing ? mdiAlertCircleOutline : methodIcon(row.type),
                            iconFill: '#ef6c00',
                            warn: warningFor(row),
                            ports: singlePort('left', false, '#ffcc80', '#fff3e0'),
                        });
                        this.addSpine(outsideHeader, row);
                    }
                }

                for (const [eventName, flowId] of mapped) {
                    const sourceId = eventCellId(eventName);
                    const targetId = methodCellId(flowId);
                    if (!this.graph.getCell(sourceId) || !this.graph.getCell(targetId))
                        continue;
                    const nameIndex = names.indexOf(eventName);
                    const accent = ACCENTS[(nameIndex < 0 ? 0 : nameIndex) % ACCENTS.length];
                    this.graph.addCell(new shapes.standard.Link({
                        id: `map:${encodeURIComponent(eventName)}`,
                        kind: 'mapping',
                        eventName,
                        source: { id: sourceId, port: 'out' },
                        target: { id: targetId, port: 'in' },
                        attrs: {
                            line: {
                                stroke: accent,
                                strokeWidth: 2,
                                targetMarker: { type: 'path', d: 'M 8 -4 0 0 8 4 z', fill: accent },
                            },
                        },
                        router: { name: MAP_ROUTER.name, args: { ...MAP_ROUTER.args } },
                        connector: { name: 'rounded', args: { radius: 8 } },
                    }));
                }
            }
            finally {
                this.paper.unfreeze();
                this.rebuilding = false;
            }
            this.applySearch();
            this.applyTreeScroll();
        },
        maxTreeScroll() {
            const stage = this.$refs.paperHost?.parentElement;
            const view = stage?.clientHeight || 0;
            return Math.max(0, (this.treeContentBottom || 0) - view);
        },
        onStageWheel(evt) {
            const bar = this.$refs.treeScroll;
            if (bar && (evt.target === bar || bar.contains(evt.target)))
                return;
            const max = this.maxTreeScroll();
            if (max <= 0)
                return;
            evt.preventDefault();
            const next = Math.max(0, Math.min(max, (this.treeScroll || 0) + (evt.deltaY || 0)));
            if (next === this.treeScroll)
                return;
            this.treeScroll = next;
            this.applyTreeScroll();
        },
        applyTreeScroll() {
            if (!this.graph || this.rebuilding)
                return;
            const offset = Math.max(0, Math.min(this.maxTreeScroll(), this.treeScroll || 0));
            this.treeScroll = offset;
            this.graph.startBatch('tree-scroll');
            for (const cell of this.graph.getElements()) {
                if (cell.get('kind') === 'event')
                    continue;
                const layoutX = cell.get('layoutX');
                const layoutY = cell.get('layoutY');
                if (typeof layoutX !== 'number' || typeof layoutY !== 'number')
                    continue;
                cell.position(layoutX, layoutY - offset);
            }
            for (const link of this.graph.getLinks()) {
                if (link.get('kind') !== 'tree')
                    continue;
                const geom = link.get('layoutGeom');
                if (!geom)
                    continue;
                link.source({ x: geom.source.x, y: geom.source.y - offset });
                link.target({ x: geom.target.x, y: geom.target.y - offset });
                link.vertices((geom.vertices || []).map((point) => ({ x: point.x, y: point.y - offset })));
            }
            this.graph.stopBatch('tree-scroll');
            const bar = this.$refs.treeScroll;
            if (bar && bar.scrollTop !== offset)
                bar.scrollTop = offset;
        },
        onTreeScrollBar() {
            const bar = this.$refs.treeScroll;
            if (!bar)
                return;
            const top = bar.scrollTop;
            if (top === (this.treeScroll || 0))
                return;
            this.treeScroll = top;
            this.applyTreeScroll();
        },
        scrollFirstMatchIntoView() {
            const query = (this.methodQuery || '').trim().toLowerCase();
            const stage = this.$refs.paperHost?.parentElement;
            if (!query || !this.graph || !stage)
                return;
            for (const cell of this.graph.getElements()) {
                if (cell.get('kind') !== 'method')
                    continue;
                const name = String(cell.get('memberName') || '').toLowerCase();
                if (!name.includes(query))
                    continue;
                const layoutY = cell.get('layoutY');
                if (typeof layoutY !== 'number')
                    return;
                const height = cell.size()?.height || 50;
                const view = stage.clientHeight;
                const visibleTop = layoutY - (this.treeScroll || 0);
                if (visibleTop >= 12 && visibleTop + height <= view - 12)
                    return;
                const target = layoutY + (height / 2) - (view / 2);
                this.treeScroll = Math.max(0, Math.min(this.maxTreeScroll(), target));
                this.applyTreeScroll();
                return;
            }
        },
        onLinkDragMove(linkView, evt) {
            if (!linkView || linkView.model.get('kind') !== 'draft')
                return;
            const native = evt?.originalEvent || evt;
            const clientX = evt?.clientX ?? native?.clientX;
            const clientY = evt?.clientY ?? native?.clientY;
            if (clientX == null || clientY == null)
                return;
            this._drag = { linkView, evt, clientX, clientY };
            this.pumpDragScroll();
        },
        pumpDragScroll() {
            if (this._dragFrame)
                return;
            const step = () => {
                this._dragFrame = null;
                const drag = this._drag;
                const stage = this.$refs.paperHost?.parentElement;
                if (!drag || !stage || !this.paper)
                    return;
                const rect = stage.getBoundingClientRect();
                const margin = 64;
                const speed = 22;
                let dy = 0;
                let dx = 0;
                if (drag.clientY < rect.top + margin)
                    dy = -Math.ceil(speed * ((rect.top + margin - drag.clientY) / margin));
                else if (drag.clientY > rect.bottom - margin)
                    dy = Math.ceil(speed * ((drag.clientY - (rect.bottom - margin)) / margin));
                if (drag.clientX < rect.left + margin)
                    dx = -Math.ceil(speed * ((rect.left + margin - drag.clientX) / margin));
                else if (drag.clientX > rect.right - margin)
                    dx = Math.ceil(speed * ((drag.clientX - (rect.right - margin)) / margin));
                if (!dx && !dy)
                    return;
                const nextTop = Math.max(0, Math.min(this.maxTreeScroll(), (this.treeScroll || 0) + dy));
                const nextLeft = Math.max(0, Math.min(stage.scrollWidth - stage.clientWidth, stage.scrollLeft + dx));
                if (nextTop === (this.treeScroll || 0) && nextLeft === stage.scrollLeft)
                    return;
                this.treeScroll = nextTop;
                stage.scrollLeft = nextLeft;
                this.applyTreeScroll();
                const local = this.paper.clientToLocalPoint(drag.clientX, drag.clientY);
                drag.linkView.dragArrowhead(drag.evt, local.x, local.y);
                this._dragFrame = requestAnimationFrame(step);
            };
            this._dragFrame = requestAnimationFrame(step);
        },
        stopLinkDrag() {
            this._drag = null;
            if (this._dragFrame) {
                cancelAnimationFrame(this._dragFrame);
                this._dragFrame = null;
            }
        },
        applySearch() {
            if (!this.graph)
                return;
            const query = (this.methodQuery || '').trim().toLowerCase();
            for (const cell of this.graph.getElements()) {
                if (cell.get('kind') !== 'method')
                    continue;
                const name = String(cell.get('memberName') || '').toLowerCase();
                const dim = !!query && !name.includes(query);
                cell.attr('root/opacity', dim ? 0.28 : 1);
            }
        },
        async createMethodForEvent(eventName, target) {
            if (this._creating || !eventName || !target || !this.model)
                return;
            this._creating = true;
            this.createHint = `Creating a method for ${eventName}…`;
            try {
                const catalog = catalogByName(eventCatalog({ channel: this.resolvedChannel() }));
                const draft = newMethodDraft({
                    eventName,
                    classEntry: {
                        name: target.get('className'),
                        methodPath: target.get('methodPath'),
                    },
                    existingNames: target.get('existingNames') || [],
                    catalogEntry: catalog.get(eventName),
                });
                if (!draft.Path)
                    throw new Error('That class has no path.');
                const uri = await resolveLogicalUri(utils.api, {
                    projectPath: projectFullPath(this.model),
                    projectName: '',
                    className: draft.className,
                    methodName: draft.Name,
                });
                const newRow = {
                    ID: utils.generateUUID(),
                    Type: 'FlowHeader',
                    Name: draft.Name,
                    Path: draft.Path,
                    Parameters: draft.Parameters,
                    ReturnType: draft.ReturnType,
                    Configuration: {
                        Method: 'GET',
                        URITemplate: uri,
                        Public: false,
                        WebAccessible: false,
                    },
                };
                const res = await utils.api.request('POST', 'Apps/FlowV2/SaveMethodHeader?Type=FlowHeader&IsNew=true', newRow);
                if (!res?.Success)
                    throw new Error(res?.Reason || 'Could not create the method');
                this.extraNames = (this.extraNames || []).filter((name) => name !== eventName);
                this.model.Events = connect(this.model.Events, eventName, newRow.ID);
                this.folderItems = [...(this.folderItems || []), newRow];
                this.createHint = '';
                this.$emit('create-method', eventName, this.model.ID || this.projectId);
                this.loadFolderItems(true);
            }
            catch (err) {
                this.createHint = err?.message || String(err);
            }
            finally {
                this._creating = false;
            }
        },
        onLinkConnect(linkView) {
            this.stopLinkDrag();
            if (this.rebuilding)
                return;
            const link = linkView?.model;
            if (!link || link.get('kind') === 'tree' || link.get('kind') === 'mapping')
                return;
            const source = this.graph.getCell(link.source()?.id);
            const target = this.graph.getCell(link.target()?.id);
            const eventName = source?.get('eventName');
            const targetKind = target?.get('kind');
            if (!eventName || !this.model) {
                this.$nextTick(() => link.remove());
                return;
            }
            if (targetKind === 'create-method') {
                this.$nextTick(() => {
                    if (link.graph)
                        link.remove();
                });
                this.createMethodForEvent(eventName, target);
                return;
            }
            if (targetKind === 'method' && target.get('dropTarget') === true) {
                this.createHint = '';
                this.extraNames = this.extraNames.filter((name) => name !== eventName);
                this.model.Events = connect(this.model.Events, eventName, target.get('flowId'));
                return;
            }
            this.$nextTick(() => {
                if (link.graph)
                    link.remove();
            });
        },
        onLinkEnter(linkView) {
            if (!linkView || linkView.model.get('kind') !== 'mapping')
                return;
            clearTimeout(this._toolTimer);
            linkView.addTools(new dia.ToolsView({
                tools: [new linkTools.Remove({ distance: '50%', offset: 0 })],
            }));
        },
        onLinkLeave(linkView) {
            this._toolTimer = setTimeout(() => {
                linkView?.removeTools?.();
            }, 200);
        },
        onCellRemove(cell) {
            if (this.rebuilding || !cell?.isLink?.() || cell.get('kind') !== 'mapping')
                return;
            const eventName = cell.get('eventName');
            if (!eventName || !this.model)
                return;
            this.model.Events = disconnect(this.model.Events, eventName);
        },
        addCustomEvent() {
            const name = (this.customDraft || '').trim();
            if (!name)
                return;
            const catalog = eventCatalog({
                channelType: this.model?.ChannelType,
                schema: this.schema,
            });
            const visible = visibleEventNames(catalog, this.model?.Events, this.extraNames);
            this.customDraft = '';
            if (visible.includes(name))
                return;
            this.extraNames = [...this.extraNames, name];
        },
    },
    render() {
        return (
            <div class="flow-v2-event-map">
                {this.folderError ? <QBanner class="bg-red-1 text-negative">{this.folderError}</QBanner> : null}
                <div class="flow-v2-event-map__toolbar">
                    <div class="flow-v2-event-map__side">
                        <span class="flow-v2-event-map__kicker">Events</span>
                        <QInput
                            dense
                            outlined
                            label="Custom event"
                            modelValue={this.customDraft}
                            onUpdate:modelValue={(value) => { this.customDraft = value; }}
                            onKeydown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    this.addCustomEvent();
                                }
                            }}
                            style={{ width: '180px', maxWidth: '100%', minWidth: 0 }}
                        />
                        <QBtn
                            unelevated
                            no-caps
                            color="primary"
                            label="Add event"
                            class={`${SENTENCE_CASE_BUTTON_CLASS} q-my-xs`}
                            onClick={() => this.addCustomEvent()}
                        />
                    </div>
                    <div class="flow-v2-event-map__side">
                        <span class="flow-v2-event-map__kicker">This project</span>
                        <QInput
                            dense
                            outlined
                            clearable
                            label="Find a method"
                            modelValue={this.methodQuery}
                            onUpdate:modelValue={(value) => { this.methodQuery = value || ''; }}
                            style={{ width: '200px', maxWidth: '100%', minWidth: 0 }}
                        />
                        <span class="flow-v2-event-map__count">{this.linkedCount} linked</span>
                    </div>
                </div>
                <div class="flow-v2-event-map__stage">
                    <div ref="paperHost" class="flow-v2-event-map__paper" />
                    <div
                        ref="treeScroll"
                        class="flow-v2-event-map__tree-scroll"
                        onScroll={() => this.onTreeScrollBar()}
                    >
                        <div style={{ height: `${this.treeContentBottom}px` }} />
                    </div>
                    {!this.foldersReady ? (
                        <div class="flow-v2-event-map__busy">
                            <QSpinner color="primary" size="32px" />
                        </div>
                    ) : null}
                </div>
                {this.createHint ? <p class="flow-v2-event-map__hint">{this.createHint}</p> : null}
            </div>
        );
    },
});
